import { JSX, useEffect } from 'react';
import useAppStore from '../stores/useAppStore';
import { uiLogger, logUIAction } from '../utils/logger';
import { useCurrencySymbol } from '../hooks/useCurrencySymbol';

// Commented out sections for MVP launch - can be restored later
// import HeroSection from '../components/sections/HeroSection';
// import FeaturesSection from '../components/sections/FeaturesSection';
// import PricingSection from '../components/sections/PricingSection';
// import FAQSection from '../components/sections/FAQSection';

export default function HomePage(): JSX.Element {
  const { openRegistrationModal, isAuthenticated } = useAppStore();
  const { currencySymbol, isLoading: currencyLoading } = useCurrencySymbol();
  const hideAuthButtons = import.meta.env.VITE_HIDE_AUTH_BUTTONS === 'true';

  useEffect(() => {
    uiLogger.info('HomePage loaded - MVP version with chat widget preserved');
    logUIAction('HomePage', 'page_load', { 
      version: 'MVP',
      features: ['chat_widget', 'registration_cta'],
      timestamp: new Date().toISOString()
    });
  }, []);

  const handleGetStartedClick = (): void => {
    logUIAction('HomePage', 'cta_click', { 
      button: 'get_started',
      location: 'hero_section',
      timestamp: new Date().toISOString()
    });
    openRegistrationModal();
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section - Clean and Simple MVP Design */}
      <section className="relative">
        <div className="container-max section-padding">
          <div className="flex flex-col items-center justify-center min-h-[80vh] text-center pt-8 sm:pt-4 md:pt-0 pb-16 sm:pb-8 md:pb-0">
            <div className="max-w-4xl mx-auto">
              {/* Main Title - Reduced size, split into two lines */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 leading-tight">
                AI That Works for Your Business
                <br />
                <span className="text-primary-600 dark:text-primary-400">
                  Not the Other Way Around
                </span>
              </h1>

              {/* First Paragraph */}
              <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto px-4 sm:px-0">
                Small businesses deserve enterprise-grade AI without the enterprise complexity. 
                Fabrik 28 transforms sophisticated AI capabilities into simple, powerful solutions 
                that enhance your existing processes in minutes, not months. Whether you're 
                scheduling appointments, managing projects, or handling customer communications, 
                our platform makes AI automation accessible to every business owner—no technical 
                expertise required.
              </p>

              {/* Second Paragraph with dynamic currency */}
              <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 sm:mb-12 leading-relaxed max-w-3xl mx-auto px-4 sm:px-0">
                Stop spending thousands on consultants or months learning complex systems. 
                Our intuitive platform connects your business tools, automates your workflows, 
                and delivers measurable results from day one. From WhatsApp automation to 
                calendar management, from invoice generation to project tracking, Fabrik 28 
                gives you the AI capabilities that used to cost hundreds of thousands of dollars, 
                starting at just{' '}
                <span className="font-semibold text-primary-600 dark:text-primary-400">
                  {currencyLoading ? 'US$99' : `${currencySymbol}99`} per month
                </span>.
              </p>

              {/* CTA Button or Launching Soon Message */}
              {!isAuthenticated && (
                <div className="flex flex-col items-center px-4 sm:px-0 mb-12 sm:mb-0">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 sm:p-8 max-w-2xl mx-auto text-center border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                      Preview Launching Soon
                    </h3>
                    <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 mb-6">
                      We're putting the finishing touches on our platform. For further information and early access, please register and get in contact with us.
                    </p>
                    
                    {/* CTA Button with nice margins */}
                    <div className="mt-6">
                      <button
                        onClick={handleGetStartedClick}
                        className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg text-base transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        Get Started Today
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* All other sections commented out for MVP launch */}
      {/*
      <HeroSection />
      <FeaturesSection />
      <PricingSection />
      <FAQSection />
      
      <section className="py-20 bg-gray-100 dark:bg-gray-800">
        <div className="container-max section-padding text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Additional sections coming soon...
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            We'll add Problem/Solution, Industries, How It Works, Customer Success, 
            Trust, and Final CTA sections to complete the marketing website.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="p-6 bg-white dark:bg-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Problem/Solution
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Highlight the 40% time waste problem and Fabrik 28's solution
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Industries
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Tabbed interface for Construction, Retail, Professional Services
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Customer Success
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Real results from Swiss businesses using Fabrik 28
              </p>
            </div>
          </div>
        </div>
      </section>
      */}
    </div>
  );
}
